'use client'

import { Slot } from '@radix-ui/react-slot'
import * as React from 'react'

import { cn } from '../cn'

/**
 * Canonical className for a single-line list-view row. Shared across the
 * My Tasks page and every home-page widget so densities never drift.
 * Exported for callers that compose it into their own element (e.g. with
 * selection/hover variants). For plain rows, prefer the <ListRow> component.
 */
export const LIST_ROW_CLASS = 'flex items-center gap-3 px-4 py-4 md:py-2 md:min-h-10'

const INTERACTIVE_CLASS = 'hover:bg-muted/50 transition-colors cursor-pointer'

export interface ListRowProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Merge styles onto a single child element instead of rendering a div. Use for react-router Link, etc. */
  asChild?: boolean
  /** Adds hover + cursor-pointer for clickable rows. */
  interactive?: boolean
}

const ListRow = React.forwardRef<HTMLDivElement, ListRowProps>(
  ({ asChild = false, interactive = false, className, ...props }, ref) => {
    const Comp = asChild ? Slot : 'div'
    return <Comp ref={ref} className={cn(LIST_ROW_CLASS, interactive && INTERACTIVE_CLASS, className)} {...props} />
  },
)
ListRow.displayName = 'ListRow'

export { ListRow }
